import {
  DateFormat,
  formatDate,
} from '@efdigital/ll-addon-content/helpers/format-date';
import type { CalendarItem } from '@efdigital/ll-addon-data';
import { queryCalendarItems } from '@efdigital/ll-admin-v2/data/builders/xceed/calendar-items';
import { fn } from '@ember/helper';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Store } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';

import { ResultsTable } from '../results-table.gts';

type Signature = {
  Args: {
    programId: string;
    cohortId: string;
    calendarItemIds: string[];
    onSelect: (calendarItems: CalendarItem[]) => void;
  };
  Blocks: {
    input: [showModal: (selectedCalendarItemIds: string[]) => void];
  };
};

function contains(array: string[], item: string | null) {
  return item && array.includes(item);
}

export default class CalendarItemPicker extends Component<Signature> {
  @tracked isOpen = false;
  @service declare store: Store;

  @tracked selectedCalendarItemIds: string[] = [];

  showModal = (selectedCalendarItemIds: string[]) => {
    this.selectedCalendarItemIds = [...selectedCalendarItemIds];
    this.isOpen = true;
  };

  modalClosed = () => {
    this.isOpen = false;
  };

  selectCalendarItem = (calendarItem: CalendarItem, event: Event) => {
    event.stopPropagation();
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.selectedCalendarItemIds = [
        ...this.selectedCalendarItemIds,
        calendarItem.id!,
      ];
    } else {
      this.selectedCalendarItemIds = this.selectedCalendarItemIds.filter(
        (id) => id !== calendarItem.id!
      );
    }
  };

  confirmSelection = (calendarItems: CalendarItem[]): void => {
    const foundSelectedCalendarItems = this.selectedCalendarItemIds
      .map((id) => calendarItems.find((item) => item.id === id))
      .filter((item): item is CalendarItem => item !== undefined);
    this.args.onSelect(foundSelectedCalendarItems);
    this.isOpen = false;
  };

  <template>
    {{yield this.showModal to="input"}}
    {{#let (uniqueId) as |uniqueId|}}
      <Request @query={{queryCalendarItems @programId @cohortId}}>
        <:content as |calendarItems|>
          <BsModal
            @open={{this.isOpen}}
            @onHidden={{this.modalClosed}}
            @size="xl"
            @renderInPlace={{true}}
            as |modal|
          >
            <modal.header>
              <h2 class="gud-eyebrow mb-2">Calendar</h2>
            </modal.header>
            <modal.body>
              <ResultsTable
                @data={{calendarItems.data}}
                class="w-100 table-hover"
              >
                <:header>
                  <th>Title</th>
                  <th>Starts</th>
                  <th>Ends</th>
                  <th>Event Type</th>
                </:header>
                <:row as |calendarItem index|>
                  {{#let
                    (contains this.selectedCalendarItemIds calendarItem.id)
                    as |isSelected|
                  }}
                    <tr class={{if isSelected "table-primary"}}>
                      <td>
                        <div class="form-check form-switch">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            checked={{isSelected}}
                            id="{{uniqueId}}-{{index}}"
                            {{on
                              "change"
                              (fn this.selectCalendarItem calendarItem)
                            }}
                          />
                          <label
                            class="form-check-label"
                            for="{{uniqueId}}-{{index}}"
                          >
                            {{calendarItem.title}}
                          </label>
                          {{#if calendarItem.cancelled}}
                            <span
                              class="badge bg-danger text-uppercase"
                            >Cancelled</span>
                          {{/if}}
                        </div>
                      </td>
                      <td>
                        {{#if calendarItem.starts}}
                          {{formatDate
                            calendarItem.starts
                            DateFormat.ShortDate
                          }}
                          at
                          {{formatDate calendarItem.starts DateFormat.TimeOnly}}
                        {{else}}
                          -
                        {{/if}}
                      </td>
                      <td>
                        {{#if calendarItem.ends}}
                          {{formatDate calendarItem.ends DateFormat.ShortDate}}
                          at
                          {{formatDate calendarItem.ends DateFormat.TimeOnly}}
                        {{else}}
                          -
                        {{/if}}
                      </td>
                      <td>{{calendarItem.eventType}}</td>
                    </tr>
                  {{/let}}
                </:row>
                <:empty>
                  <p>The calendar is empty.</p>
                </:empty>
              </ResultsTable>
            </modal.body>
            <modal.footer class="text-center">
              Selected
              {{this.selectedCalendarItemIds.length}}
              of
              {{calendarItems.data.length}}
              <button
                type="button"
                class="btn btn-primary btn-md"
                {{on "click" (fn this.confirmSelection calendarItems.data)}}
              >Select</button>
              <button
                type="button"
                class="btn btn-outline-primary btn-md"
                {{on "click" this.modalClosed}}
              >Cancel</button>
            </modal.footer>
          </BsModal>
        </:content>
        <:error as |error|>
          <p>Error: {{error.message}}</p>
        </:error>
      </Request>
    {{/let}}
  </template>
}
